/**
 * Audit Archive Service
 * Phase 1 - Multi-User Auth Foundation
 * Manages audit log capacity, archival, and export
 */

const STORAGE_KEY = 'sr.audit.events';
const ARCHIVE_PREFIX = 'sr.audit.archive_';

const AUDIT_CONFIG = {
  MAX_EVENTS: 1000,
  WARNING_THRESHOLD: 800,  // 80%
  CRITICAL_THRESHOLD: 900, // 90%
  MAX_ARCHIVES: 5,
  ARCHIVE_SIZE: 500  // Archive oldest 50% when at 90%
};

/**
 * Get all audit events from localStorage
 */
function getAuditEvents() {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Failed to load audit events:', error);
    return [];
  }
}

/**
 * Save audit events to localStorage
 */
function saveAuditEvents(events) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  } catch (error) {
    console.error('Failed to save audit events:', error);
  }
}

/**
 * Get list of archive keys
 */
function getArchiveKeys() {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(ARCHIVE_PREFIX)) {
      keys.push(key);
    }
  }
  return keys.sort();
}

/**
 * Check current audit log capacity
 * @returns {Object} Capacity information
 */
export function checkAuditCapacity() {
  const events = getAuditEvents();
  const archives = getArchiveKeys();

  const percentage = (events.length / AUDIT_CONFIG.MAX_EVENTS) * 100;
  let status = 'normal';

  if (events.length >= AUDIT_CONFIG.CRITICAL_THRESHOLD) {
    status = 'critical';
  } else if (events.length >= AUDIT_CONFIG.WARNING_THRESHOLD) {
    status = 'warning';
  }

  return {
    current: events.length,
    max: AUDIT_CONFIG.MAX_EVENTS,
    percentage,
    status,
    archives: archives.length
  };
}

/**
 * Archive old events when capacity is reached
 */
export function archiveAuditLog() {
  const events = getAuditEvents();

  if (events.length < AUDIT_CONFIG.CRITICAL_THRESHOLD) {
    console.log('Archive not needed yet', {
      current: events.length,
      threshold: AUDIT_CONFIG.CRITICAL_THRESHOLD
    });
    return;
  }

  const archiveSize = AUDIT_CONFIG.ARCHIVE_SIZE;
  const toArchive = events.slice(0, archiveSize);
  const remaining = events.slice(archiveSize);

  if (toArchive.length === 0) {
    console.warn('No events to archive');
    return;
  }

  // Create archive
  const archive = {
    archivedAt: Date.now(),
    eventCount: toArchive.length,
    events: toArchive,
    startDate: toArchive[0]?.ts || toArchive[0]?.timestamp || Date.now(),
    endDate: toArchive[toArchive.length - 1]?.ts || toArchive[toArchive.length - 1]?.timestamp || Date.now()
  };

  const archiveKey = `${ARCHIVE_PREFIX}${Date.now()}`;

  try {
    localStorage.setItem(archiveKey, JSON.stringify(archive));
    saveAuditEvents(remaining);
    cleanOldArchives();

    console.log(`Archived ${archiveSize} events`, {
      archiveKey,
      remaining: remaining.length
    });
  } catch (error) {
    console.error('Failed to create archive:', error);
  }
}

/**
 * Clean old archives (keep only MAX_ARCHIVES)
 */
function cleanOldArchives() {
  const archives = getArchiveKeys();

  if (archives.length <= AUDIT_CONFIG.MAX_ARCHIVES) {
    return;
  }

  // Archives are sorted by key (timestamp), oldest first
  const toDelete = archives.slice(0, archives.length - AUDIT_CONFIG.MAX_ARCHIVES);

  for (const key of toDelete) {
    try {
      localStorage.removeItem(key);
      console.log(`Deleted old archive: ${key}`);
    } catch (error) {
      console.error(`Failed to delete archive ${key}:`, error);
    }
  }
}

/**
 * Get all archives metadata
 * @returns {Array} Archive metadata list
 */
export function getArchives() {
  const archiveKeys = getArchiveKeys();
  const archives = [];

  for (const key of archiveKeys) {
    try {
      const data = localStorage.getItem(key);
      if (data) {
        const archive = JSON.parse(data);
        archives.push({
          key,
          archivedAt: archive.archivedAt,
          eventCount: archive.eventCount,
          startDate: archive.startDate,
          endDate: archive.endDate
        });
      }
    } catch (error) {
      console.error(`Failed to read archive ${key}:`, error);
    }
  }

  return archives.sort((a, b) => b.archivedAt - a.archivedAt);
}

/**
 * Export specific archive
 * @param {string} archiveKey - Archive key
 * @returns {Array} Archived events
 */
export function exportArchive(archiveKey) {
  try {
    const data = localStorage.getItem(archiveKey);
    if (data) {
      const archive = JSON.parse(data);
      return archive.events || [];
    }
  } catch (error) {
    console.error(`Failed to export archive ${archiveKey}:`, error);
  }
  return [];
}

/**
 * Export all events (active + all archives) for MCA inspection
 * @param {string} format - 'json' or 'csv'
 * @returns {string} Exported data
 */
export function exportAll(format = 'json') {
  const archiveKeys = getArchiveKeys();
  let allEvents = [];

  // Load all archived events (oldest first)
  for (const key of archiveKeys) {
    try {
      const data = localStorage.getItem(key);
      if (data) {
        const archive = JSON.parse(data);
        if (archive.events) {
          allEvents = [...allEvents, ...archive.events];
        }
      }
    } catch (error) {
      console.error(`Failed to load archive ${key}:`, error);
    }
  }

  // Add current events
  const currentEvents = getAuditEvents();
  allEvents = [...allEvents, ...currentEvents];

  if (format === 'json') {
    return JSON.stringify(allEvents, null, 2);
  }

  // CSV format
  const headers = ['id', 'timestamp', 'action', 'collection', 'recordId', 'actor', 'vesselId'];
  const rows = allEvents.map(e => [
    e.id || '',
    e.ts || new Date(e.timestamp).toISOString(),
    e.action || '',
    e.collection || '',
    e.recordId || '',
    e.actor?.userId || e.actor?.email || '',
    e.vesselId || ''
  ]);

  return [headers.join(','), ...rows.map(r => r.map(cell => `"${cell}"`).join(','))].join('\n');
}

/**
 * Audit archive service singleton
 */
export const auditArchive = {
  checkCapacity: checkAuditCapacity,
  archive: archiveAuditLog,
  getArchives,
  exportArchive,
  exportAll
};

export default auditArchive;
